import React, { useEffect, useRef, useState } from 'react';
import { Animated, ColorValue, Easing, Platform, StyleProp, View, ViewProps, ViewStyle } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Label, { LabelType } from './Label';
import useCustomTheme from '../../hooks/theme-hook';
import useTranslation from '../../hooks/translation-hook';

export interface LoadingProps extends ViewProps {
  style?: StyleProp<ViewStyle>;
  opacity?: string;
  textVariant?: LabelType;
  iconColor?: ColorValue;
  text?: string;
}

const useNativeAnimationDriver = Platform.OS !== 'web';

const Loading = (props: LoadingProps) => {
  const theme = useCustomTheme();
  const t = useTranslation();
  const [textVariant, setTextVariant] = useState(props.textVariant);
  const [iconColor, setIconColor] = useState(props.iconColor);
  const spin = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    setTextVariant(props.textVariant);
  }, [props.textVariant]);

  useEffect(() => {
    setIconColor(props.iconColor);
  }, [props.iconColor]);

  useEffect(() => {
    const animation = Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: 900,
        easing: Easing.linear,
        useNativeDriver: useNativeAnimationDriver,
      })
    );

    animation.start();

    return () => {
      animation.stop();
      spin.setValue(0);
    };
  }, [spin]);

  const spinInterpolation = spin.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  return (
    <View
      {...props}
      style={[
        {
          position: 'absolute',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          flexGrow: 1,
          width: '100%',
          height: '100%',
          backgroundColor: theme.isDark ? 'rgba(24, 25, 26, 0.10)' : 'rgba(255, 255, 255, 0.10)',
          padding: 8,
        },
        props.style ?? {},
      ]}
    >
      <Animated.View style={{ marginRight: 8, transform: [{ rotate: spinInterpolation }] }}>
        <FontAwesome name="refresh" size={18} color={iconColor?.toString() ?? theme.secondaryLight.toString()} />
      </Animated.View>
      <Label variant={textVariant ?? LabelType.titleLight}>{props.text ?? `${t('loading')}...`}</Label>
    </View>
  );
};

export default Loading;
